import React, { useState, useRef, useCallback, useEffect } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Button, 
  Upload, 
  message, 
  Space, 
  Typography, 
  Select,
  Input,
  Tag,
  Table,
  Alert,
  Divider
} from 'antd';
import { 
  UploadOutlined, 
  PlayCircleOutlined, 
  EyeOutlined,
  CalculatorOutlined,
  BranchesOutlined,
  ThunderboltOutlined,
  RadiusSettingOutlined,
  FunctionOutlined,
  InfoCircleOutlined,
  ClearOutlined,
} from '@ant-design/icons';
import type { UploadProps } from 'antd';
import AdvancedMolStarViewer, { AdvancedMolStarViewerRef } from '../../components/structure-analysis/AdvancedMolStarViewer';
import SequenceViewer from '../../components/structure-analysis/SequenceViewer';
import { extractSequenceFromPDB, extractSequenceFromCIF } from '../../components/structure-analysis/utils/pdbParser';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

interface StructureFile {
  name: string;
  content: string;
  type: 'pdb' | 'cif' | 'sdf';
}

interface AnalysisResult {
  id: string;
  type: string;
  data: any;
  timestamp: string;
}

const StructureAnalysisPage: React.FC = () => {
  const [structureFiles, setStructureFiles] = useState<StructureFile[]>([]);
  const [currentStructure, setCurrentStructure] = useState<StructureFile | null>(null);
  const [analysisResults, setAnalysisResults] = useState<AnalysisResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [representation, setRepresentation] = useState<string>('cartoon');
  const [colorScheme, setColorScheme] = useState<string>('chain-id');
  const [pdbSearchValue, setPdbSearchValue] = useState<string>('');
  const [extractedChains, setExtractedChains] = useState<any[]>([]);
  const [showSequencePanel, setShowSequencePanel] = useState(true);
  const [selectedChain, setSelectedChain] = useState<string>('all');
  const [chainOptions, setChainOptions] = useState<{ value: string; label: string }[]>([
    { value: 'all', label: '全部' },
  ]);
  const molstarRef = useRef<AdvancedMolStarViewerRef>(null);
  const [structureHover, setStructureHover] = useState<{ chainId: string; position: number; name?: string } | null>(null);
  const [seqHoverLabel, setSeqHoverLabel] = useState<string>('');
  const [activeHoverSource, setActiveHoverSource] = useState<'sequence' | 'structure' | null>(null);



  // 文件上传处理
  const handleFileUpload: UploadProps['customRequest'] = async (options) => {
    const { file, onSuccess, onError } = options;
    
    try {
      setLoading(true);
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const content = e.target?.result as string;
        const fileName = (file as File).name;
        const fileExtension = fileName.split('.').pop()?.toLowerCase() as 'pdb' | 'cif' | 'sdf';
        
        if (!['pdb', 'cif', 'sdf'].includes(fileExtension)) {
          message.error('不支持的文件格式，请上传 PDB、CIF 或 SDF 文件');
          return;
        }
        
        const newStructure: StructureFile = {
          name: fileName,
          content,
          type: fileExtension
        };
        
        setStructureFiles(prev => [...prev, newStructure]);
        setCurrentStructure(newStructure);
        
        message.success(`文件 ${fileName} 上传成功`);
        onSuccess?.(content);
      };
      
      reader.onerror = () => {
        message.error('文件读取失败');
        onError?.(new Error('文件读取失败'));
      };
      
      reader.readAsText(file as File);
    } catch (error) {
      message.error('上传失败');
      onError?.(error as Error);
    } finally {
      setLoading(false);
    }
  };

  // PDB搜索处理
  const handlePdbSearch = async () => {
    if (!pdbSearchValue.trim()) {
      message.warning('请输入PDB ID');
      return;
    }
    
    try {
      setLoading(true);
      const response = await fetch(`https://files.rcsb.org/download/${pdbSearchValue.toUpperCase()}.pdb`);
      
      if (!response.ok) {
        throw new Error(`PDB ${pdbSearchValue} 不存在或无法访问`);
      }
      
      const content = await response.text();
      const newStructure: StructureFile = {
        name: `${pdbSearchValue.toUpperCase()}.pdb`,
        content,
        type: 'pdb'
      };
      
      setStructureFiles(prev => [...prev, newStructure]);
      setCurrentStructure(newStructure);
      
      message.success(`PDB ${pdbSearchValue.toUpperCase()} 加载成功`);
      setPdbSearchValue('');
    } catch (error) {
      message.error(`加载PDB失败: ${error}`);
    } finally {
      setLoading(false);
    }
  };

  // 分析处理
  const handleAnalysis = useCallback((type: string, data: any) => {
    const result: AnalysisResult = {
      id: Date.now().toString(),
      type,
      data,
      timestamp: new Date().toLocaleString()
    };
    setAnalysisResults(prev => [result, ...prev]);
    message.success(`${type} 分析完成`);
  }, []);

  // 执行分析
  const performAnalysis = (type: string) => {
    if (!currentStructure) {
      message.warning('请先加载结构数据');
      return;
    }
    
    // 模拟分析结果
    const mockData: { [key: string]: any } = {
      'distance': { value: '12.5 Å', atoms: ['CA-123', 'CB-456'] },
      'angle': { value: '109.5°', atoms: ['CA-123', 'CB-456', 'CG-789'] },
      'dihedral': { value: '180.0°', atoms: ['CA-123', 'CB-456', 'CG-789', 'CD-012'] },
      'salt-bridges': { count: 3, pairs: [['ARG-45', 'ASP-67'], ['LYS-89', 'GLU-123']] },
      'disulfide-bonds': { count: 2, pairs: [['CYS-12', 'CYS-34'], ['CYS-56', 'CYS-78']] },
      'charged-residues': { positive: 15, negative: 12, total: 27 },
      'molecular-surface': { area: '1234.5 Ų', volume: '5678.9 ų' }
    };
    
    handleAnalysis(type, mockData[type] || {});
  };

  // 清除所有分析结果
  const clearAnalysisResults = useCallback(() => {
    setAnalysisResults([]);
  }, []);

  // 处理表示方式变化
  const handleRepresentationChange = useCallback((value: string) => {
    setRepresentation(value);
    if (molstarRef.current) {
      molstarRef.current.setRepresentation(value);
    }
  }, []);

  // 处理颜色方案变化
  const handleColorSchemeChange = useCallback((value: string) => {
    setColorScheme(value);
    if (molstarRef.current) {
      molstarRef.current.setColorScheme(value);
    }
  }, []);

  // 重置视图
  const handleResetView = useCallback(() => {
    if (molstarRef.current) {
      molstarRef.current.resetView();
    }
  }, []);

  // 截图功能
  const handleScreenshot = useCallback(async () => {
    try {
      const dataUrl = await molstarRef.current?.screenshot();
      if (dataUrl) {
        const a = document.createElement('a');
        const filename = `${currentStructure?.name || 'structure'}_${Date.now()}.png`;
        a.href = dataUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        message.success('截图已下载');
      } else {
        message.warning('无法获取截图');
      }
    } catch (e) {
      message.error('截图失败');
    }
  }, [currentStructure]);

  // 当结构变化时，解析链并更新下拉选项
  useEffect(() => {
    if (currentStructure?.content) {
      try {
        const isCif = (currentStructure.type === 'cif') || /_atom_site\./.test(currentStructure.content);
        const chains = isCif ? extractSequenceFromCIF(currentStructure.content) : extractSequenceFromPDB(currentStructure.content);
        const options = [
          { value: 'all', label: '全部' },
          ...chains.map(c => ({ value: c.id, label: `Chain ${c.id}` })),
        ];
        setChainOptions(options);
        // 如果当前选择在新选项中不存在，则重置为 all
        if (!options.some(o => o.value === selectedChain)) {
          setSelectedChain('all');
        }
      } catch (e) {
        setChainOptions([{ value: 'all', label: '全部' }]);
        setSelectedChain('all');
      }
    } else {
      setChainOptions([{ value: 'all', label: '全部' }]);
      setSelectedChain('all');
    }
  }, [currentStructure]);

  return (
    <div style={{ padding: '24px', minHeight: '100vh' }}>
      {/* PyMOL 风格布局：左侧可视化区域 + 右侧控制面板 */}
      <Row gutter={16}>
        {/* 左侧：纯可视化区域 */}
        <Col span={20}>
          <div style={{ 
            background: '#fff', 
            borderRadius: '4px',
            overflow: 'hidden',
            height: 'calc(100vh - 180px)',
            minHeight: '600px',
            position: 'relative',
            border: '1px solid #e0e0e0'
          }}>
            {currentStructure ? (
              <div style={{ height: '100%', position: 'relative' }}>
                {/* 序列栏（始终展示头，内容可折叠） */}
                <div style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  right: 0,
                  zIndex: 4,
                }}>
                  <SequenceViewer
                    pdbData={currentStructure.content}
                    fileName={currentStructure.name}
                    compact
                    selectedChainId={selectedChain}
                    externalHovered={activeHoverSource === 'structure' ? structureHover : null}
                    representation={representation}
                    colorScheme={colorScheme}
                    onRepresentationChange={handleRepresentationChange}
                    onColorSchemeChange={handleColorSchemeChange}
                    onSelectedChainChange={setSelectedChain}
                    onResetView={handleResetView}
                    onScreenshot={handleScreenshot}
                    fileOptions={structureFiles.map(f => ({ value: f.name, label: f.name }))}
                    selectedFile={currentStructure.name}
                    onSelectedFileChange={(name) => {
                      const file = structureFiles.find(f => f.name === name);
                      if (file) setCurrentStructure(file);
                    }}
                    height={150}
                    collapsed={!showSequencePanel}
                    sequenceActive={showSequencePanel}
                    onToggleSequence={() => setShowSequencePanel(prev => !prev)}
                    onResidueHover={(res) => {
                      if (!molstarRef.current) return;
                      if (res) {
                        // 格式化残基名称（将三字母代码转换为更友好的显示）
                        const residueName = res.name || res.code || '???';
                        const label = `${residueName} ${res.position} | Chain ${res.chainId}`;
                        setSeqHoverLabel(label);
                        setActiveHoverSource('sequence');
                        // 在3D结构中高亮对应的残基
                        molstarRef.current.highlightResidues([res.position], res.chainId, { 
                          showLabel: true, 
                          labelText: label 
                        });
                      } else {
                        setSeqHoverLabel('');
                        // 仅当当前主导源是序列时才清除3D高亮，避免与结构悬浮切换时互相打断
                        if (activeHoverSource !== 'structure') {
                          molstarRef.current.clearHighlight();
                          setActiveHoverSource(null);
                        }
                      }
                    }}
                  />
                </div>
                
                {/* 3D 可视化区域 */}
                <div style={{ 
                  height: '100%', 
                  paddingTop: showSequencePanel ? '150px' : '0px',
                  transition: 'padding-top 0.3s ease'
                }}>
                  <AdvancedMolStarViewer
                    ref={molstarRef}
                    structureData={currentStructure.content}
                    structureFormat={currentStructure.type}
                    height="100%"
                    representation={representation}
                    colorScheme={colorScheme}
                    showControls={false}
                    onStructureHover={(info) => {
                      setStructureHover(info);
                      if (info) {
                        // 结构悬浮接管主导权时，清理序列产生的3D高亮，避免残留
                        try { molstarRef.current?.clearHighlight(); } catch {}
                        setSeqHoverLabel('');
                        setActiveHoverSource('structure');
                      } else {
                        setActiveHoverSource(prev => prev === 'structure' ? null : prev);
                      }
                    }}
                    externalHoverLabel={activeHoverSource === 'sequence' ? seqHoverLabel : (activeHoverSource === 'structure' && structureHover ? `${structureHover.name || ''} ${structureHover.position} | Chain ${structureHover.chainId}` : undefined)}
                    onResidueSelect={(residueIds) => {
                      console.log('从3D结构选中的残基:', residueIds);
                    }}
                  />
                </div>
                {/* 全局右下角浮层（序列悬停或3D悬停时显示） */}
                {(() => {
                  const floatingLabel = activeHoverSource === 'sequence'
                    ? (seqHoverLabel || '')
                    : (activeHoverSource === 'structure' && structureHover
                      ? `${structureHover.name || ''} ${structureHover.position} | Chain ${structureHover.chainId}`
                      : '');
                  return floatingLabel ? (
                  <div style={{
                    position: 'absolute',
                    right: 16,
                    bottom: 16,
                    background: 'linear-gradient(135deg, rgba(255, 77, 79, 0.95) 0%, rgba(220, 20, 60, 0.95) 100%)',
                    color: '#fff',
                    padding: '10px 14px',
                    borderRadius: 8,
                    fontSize: 13,
                    fontFamily: 'monospace',
                    fontWeight: 600,
                    zIndex: 2000,
                    pointerEvents: 'none',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.1) inset',
                    backdropFilter: 'blur(4px)',
                    letterSpacing: '0.5px',
                    lineHeight: '1.5',
                    minWidth: '180px',
                    textAlign: 'center'
                  }}>
                    <div>{floatingLabel}</div>
                  </div>
                  ) : null;
                })()}
              </div>
            ) : (
              <div 
                style={{ 
                  height: '100%', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  background: '#fafafa'
                }}
              >
                <div style={{ textAlign: 'center' }}>
                  <BranchesOutlined style={{ fontSize: '64px', color: '#d9d9d9', marginBottom: '24px' }} />
                  <div>
                    <Text style={{ color: '#999', fontSize: '16px' }}>请上传结构文件或从PDB数据库加载</Text>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Col>
        
        {/* 右侧：控制面板 */}
        <Col span={4}>
          <div style={{ height: 'calc(100vh - 180px)', overflowY: 'auto', paddingRight: '8px' }}>
            {/* 文件加载 */}
            <Card title="加载结构" size="small" style={{ marginBottom: 12 }}>
              <Upload.Dragger
                name="file"
                multiple={true}
                customRequest={handleFileUpload}
                showUploadList={false}
                accept=".pdb,.cif,.sdf"
                style={{ marginBottom: 12 }}
              >
                <p className="ant-upload-drag-icon" style={{ margin: '8px 0' }}>
                  <UploadOutlined style={{ fontSize: '24px' }} />
                </p>
                <p className="ant-upload-text" style={{ margin: 0, fontSize: '12px' }}>上传文件</p>
              </Upload.Dragger>
              
              <Space.Compact style={{ width: '100%' }}>
                <Input
                  placeholder="PDB ID"
                  value={pdbSearchValue}
                  onChange={(e) => setPdbSearchValue(e.target.value.toUpperCase())}
                  onPressEnter={handlePdbSearch}
                  size="small"
                />
                <Button 
                  type="primary" 
                  icon={<PlayCircleOutlined />}
                  loading={loading}
                  onClick={handlePdbSearch}
                  size="small"
                />
              </Space.Compact>
            </Card>
            {structureFiles.length > 0 && (
              <Card 
                title="已加载" 
                size="small" 
                style={{ marginBottom: 12 }}
                extra={
                  <Space>
                    <Button size="small" danger onClick={() => { setStructureFiles([]); setCurrentStructure(null); message.success('已清空'); }}>
                      清空
                    </Button>
                  </Space>
                }
              >
                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {structureFiles.map((file) => (
                    <Tag 
                      key={file.name}
                      color={currentStructure?.name === file.name ? 'blue' : 'default'}
                      closable
                      onClose={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setStructureFiles(prev => {
                          const newFiles = prev.filter(f => f.name !== file.name);
                          if (currentStructure?.name === file.name) {
                            setCurrentStructure(newFiles.length ? newFiles[0] : null);
                          }
                          return newFiles;
                        });
                      }}
                      style={{ 
                        cursor: 'pointer', 
                        marginBottom: 4, 
                        width: '100%', 
                        textAlign: 'left',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                      onClick={() => setCurrentStructure(file)}
                    >
                      {file.name}
                    </Tag>
                  ))}
                </div>
              </Card>
            )}
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default StructureAnalysisPage;
